import type { FieldValues, UseFormReturn } from 'react-hook-form';

import { ApiError, NetworkError, detailLines } from '../api/errors';

const pathsOf = (value: unknown, prefix = ''): Set<string> => {
  const paths = new Set<string>();
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return paths;

  for (const [key, child] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key;
    paths.add(path);
    for (const nested of pathsOf(child, path)) paths.add(nested);
  }
  return paths;
};

export interface IFormRejection {
  message: string;
  detail?: string;
  orphans: readonly string[];
}

const NETWORK_MESSAGE =
  'The request never left. Nothing was saved - try again, and check your connection.';

export const applyApiError = <TValues extends FieldValues>(
  form: UseFormReturn<TValues>,
  error: unknown,
): IFormRejection => {
  if (error instanceof NetworkError) return { message: NETWORK_MESSAGE, orphans: [] };
  if (!(error instanceof ApiError)) return { message: String(error), orphans: [] };
  if (error.signedOut) return { message: error.message, orphans: [] };

  const rendered = pathsOf(form.getValues());
  const attachable = error.fieldErrors.filter((fieldError) => rendered.has(fieldError.field));
  const orphans = [
    ...error
      .unattached([...rendered])
      .map((fieldError) => `${fieldError.field} - ${fieldError.message}`),
    ...detailLines(error.rawDetails),
  ];

  for (const fieldError of attachable) {
    // `shouldFocus` on the first one only: focusing each in turn lands the caret on the last complaint rather than the first to fix.
    form.setError(
      fieldError.field as never,
      { type: 'server', message: fieldError.message },
      { shouldFocus: fieldError === attachable[0] },
    );
  }

  const detail = [error.status, error.code, error.requestId].filter(Boolean).join(' · ');

  if (attachable.length > 0 && orphans.length === 0) return { message: '', detail, orphans: [] };

  return { message: error.message, detail, orphans };
};
